// eslint-disable-file no-use-before-define
import ajax from '@/lib/ajax';
import cookieUtils from '@/lib/cookieUtils';
import JwtToken from '@/models/Auth/JwtToken';
import { Module, Mutation, Action, VuexModule, getModule } from 'vuex-module-decorators';
import store from '@/store';
import NavigationMenuItem from '@/models/Shared/NavigationMenuItem'
import NavigationMenu from '@/models/Shared/NavigationMenu';
import NavigationScope from '@/models/Shared/NavigationScope';
import UserProfileDto from '@/models/Users/UserProfileDto';
import CompanyDto from '@/models/Companies/CompanyDto';
import ErrorMessage from '@/models/Shared/ErrorMessage';
import Errors from '@/models/Shared/Errors';
import IconConstants from '@/lib/iconConstants';
import { Permissions, canView } from '@/lib/permissions';
import AnonAuthenticateRequest from '@/models/Auth/AnonAuthenticateRequest';
import { WorkerAnonModule } from '@/store/modules/workerAnon';
import { FirebaseError } from 'firebase/app';
import { getAuth, User as FirebaseUser, signInWithEmailAndPassword, signOut, signInWithCustomToken } from "firebase/auth";
import TriggerPasswordResetRequest from '@/models/Auth/TriggerPasswordReset';
import AgencyContextDto from '@/models/Agencies/AgencyContextDto';
import { generateCodeVerifier, generateCodeChallenge, generateState, generateNonce, storePKCEData, buildEntraAuthUrl } from '@/lib/pkce';
import { cleanAllAgencyScopedStores } from '@/lib/storeCleanup';

export interface IAppState {
  navigationDrawerIsMini: boolean;
  navigationMenu: NavigationMenu;
  userProfile: UserProfileDto;
  passwordReset: boolean;
  currentAgencyContext: AgencyContextDto;
  dialogLoading: boolean;
  lastError: ErrorMessage;
  lastErrorVisible: boolean;
  currentUser: FirebaseUser;
  authChecked: boolean;
}

@Module({ dynamic: true, store, name: 'App', namespaced: true })
class App extends VuexModule implements IAppState {

  public navigationDrawerIsMini: boolean = JSON.parse(localStorage.getItem('app.navigationDrawerIsMini'));

  public navigationMenu: NavigationMenu = JSON.parse(localStorage.getItem('app.navigationMenu'));

  public userProfile: UserProfileDto = JSON.parse(localStorage.getItem('app.userProfile'));

  public passwordReset = false;

  public currentAgencyContext: AgencyContextDto = JSON.parse(localStorage.getItem('app.currentAgencyContext'));

  public dialogLoading = false;

  public lastError: ErrorMessage = null;

  public lastErrorVisible = false;

  public currentUser: FirebaseUser = null;

  public authChecked = false;

  @Action
  async clearErrorMessage() {
    this.context.commit("setErrorMessage", null);
  }

  @Action
  async updateErrorMessage(errorMessage: ErrorMessage) {
    this.context.commit("setErrorMessage", errorMessage);
  }

  @Action
  async handleApiError(error: any) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx. response.Data, response.status and response.headers available
      if (error.response.data.code == Errors.OutOfDate || error.response.data.code == Errors.NotFound) {
        this.context.commit("setErrorMessage", new ErrorMessage(error.response.data.message, error.response.data.code, true));
      } else {
        this.context.commit("setErrorMessage", new ErrorMessage(error.response.data.message, error.response.data.code, false));
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.CompanyRequest in node.js
      this.context.commit("setErrorMessage", new ErrorMessage("Server unavailable.", "SERVER_UNAVAILABLE", false));
    } else {
      // Something happened in setting up the request that triggered an Error. error.Message available
      console.error('[API Error] Request setup failed:', error.message, error.stack, error);
      this.context.commit("setErrorMessage", new ErrorMessage("Request could not be sent: " + (error.message || 'Unknown error'), "REQUEST_SETUP_FAILED", false));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  @Action
  async handleFirebaseError(error: FirebaseError) {
    let errorMessage = 'An unknown error occured. Please contact support@giganaut.com';
    const errorCode = 'Authentication Error';
    console.log(error);
    switch(error.code) {
      case 'auth/user-disabled':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        errorMessage = 'Invalid username or password.'
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed login attempts. Try again later or reset your password.';
        break;
      case 'auth/invalid-action-code':
        errorMessage = 'This link has expired.'
        break;
      case 'auth/popup-closed-by-user':
        // User closed the popup - don't show error
        return;
      case 'auth/cancelled-popup-request':
        // User opened another popup - don't show error
        return;
    }
    this.context.commit("setErrorMessage", new ErrorMessage(errorMessage, errorCode, false));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  

  @Action
  async getAgencyContext(agencyId: string) {
    // When switching to a different agency (sysadmin moving between
    // tenants), wipe all cached agency-scoped Vuex state up-front.
    // Without this, pickers like rate cards / location types kept rows
    // from the previous agency and crossed the streams on the next
    // create/convert. Same agency → no-op so a plain refresh of the
    // current context doesn't bin pre-loaded list state.
    const currentId = this.currentAgencyContext ? this.currentAgencyContext.id : null;
    if (currentId && currentId !== agencyId) {
      cleanAllAgencyScopedStores();
    }
    await ajax.get('/api/v1/sys/agency-context/' + agencyId)
    .then(response => {
      this.context.commit("persistAgencyContext", response.data);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  // @Action
  // async setCompanyContext(companyId: string) {
  //   var request = new SetAgencyContextRequest()
  //   request.agencyId = this.currentAgencyContext.id;
  //   request.companyId = companyId;
  //   await ajax.post('/api/v1/sys/agency-context', request)
  //   .then(response => {
  //     this.context.commit("persistSetAgencyContextResponse", response.data);
  //   })
  //   .catch(error => {
  //     AppModule.handleApiError(error);
  //   });
  // }

  @Action
  async firebaseLogin(userInfo: { username: string; password: string}) {
    console.log('[Firebase Auth] Starting signInWithEmailAndPassword at:', new Date().toISOString());
    const auth = getAuth();
    await signInWithEmailAndPassword(auth, userInfo.username, userInfo.password)
      .then((userIdentity) => {
        console.log('[Firebase Auth] signInWithEmailAndPassword complete at:', new Date().toISOString());
        this.context.commit("setCurrentUser", userIdentity.user);
      })
      .catch((error) => {
        console.log('[Firebase Auth] signInWithEmailAndPassword error at:', new Date().toISOString());
        AppModule.handleFirebaseError(error);
      });
  }

  @Action
  async firebaseLogOut() {
    // Call backend to revoke Firebase sessions and delete stored tokens
    try {
      if (AppModule.currentUser) {
        await ajax.post('/api/v1/agency/auth/logout');
      }
    } catch (error) {
      // Continue with client-side logout even if backend call fails
      console.warn('[Auth] Backend logout failed:', error);
    }

    // Flag that the user explicitly logged out so the next SSO login
    // forces re-authentication (prompt=login) instead of silently reusing
    // the existing Microsoft Entra browser session
    sessionStorage.setItem('sso.explicit_logout', 'true');

    // Sign out from Firebase client-side
    const auth = getAuth()
    signOut(auth)
      .then(() => {
        this.context.commit("setCurrentUser", null);
      })
      .catch((error: FirebaseError) => {
        AppModule.handleFirebaseError(error);
      });
  }

  @Action
  async lookupAuthProvider(email: string): Promise<string> {
    try {
      const response = await ajax.get(`/api/v1/public/auth/provider-lookup?email=${encodeURIComponent(email)}`);
      const data = response.data;

      return data.provider;
    } catch (error) {
      AppModule.handleApiError(error);
      throw error;
    }
  }

  @Action
  async initiateEntraLogin(email: string): Promise<void> {
    try {
      // Fetch Entra config from backend (multi-tenant app config)
      const configResponse = await ajax.get(`/api/v1/public/auth/entra/config?email=${encodeURIComponent(email)}`);
      const { tenantId, clientId } = configResponse.data;

      if (!tenantId || !clientId) {
        this.context.commit("setErrorMessage", new ErrorMessage('SSO configuration not found. Please try again.', 'SSO_CONFIG_ERROR', false));
        throw new Error('Missing SSO configuration');
      }

      // Generate PKCE parameters and OIDC nonce
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();
      const nonce = generateNonce();

      // Store PKCE data and nonce for callback
      storePKCEData(codeVerifier, state, email, nonce);

      // If the user explicitly logged out, force Microsoft to show the login screen
      // instead of silently reusing the existing Entra browser session
      const forceLogin = sessionStorage.getItem('sso.explicit_logout') === 'true';
      sessionStorage.removeItem('sso.explicit_logout');

      // Build and redirect to Microsoft Entra authorization URL
      const redirectUri = `${window.location.origin}/auth/callback`;
      const authUrl = buildEntraAuthUrl(tenantId, clientId, redirectUri, codeChallenge, state, email, nonce, forceLogin);

      // Redirect to Microsoft Entra
      window.location.href = authUrl;
    } catch (error) {
      AppModule.handleApiError(error);
      throw error;
    }
  }

  @Action
  async exchangeEntraCode(params: { code: string; codeVerifier: string; email: string; nonce: string }): Promise<void> {
    const { code, codeVerifier, email, nonce } = params;

    try {
      // Exchange authorization code for Firebase custom token
      const redirectUri = `${window.location.origin}/auth/callback`;
      const response = await ajax.post('/api/v1/public/auth/entra/token', {
        authorizationCode: code,
        redirectUri: redirectUri,
        codeVerifier: codeVerifier,
        email: email,
        nonce: nonce
      });

      const { firebaseCustomToken } = response.data;

      // Sign in to Firebase with the custom token
      const auth = getAuth();
      const userCredential = await signInWithCustomToken(auth, firebaseCustomToken);
      this.context.commit("setCurrentUser", userCredential.user);

      // Store SSO session metadata for periodic re-validation
      localStorage.setItem('sso.lastValidatedAt', new Date().toISOString());
      localStorage.setItem('sso.email', email);

    } catch (error: any) {
      if (error.response) {
        AppModule.handleApiError(error);
      } else if (error.code) {
        AppModule.handleFirebaseError(error as FirebaseError);
      } else {
        this.context.commit("setErrorMessage", new ErrorMessage('Authentication failed. Please try again.', 'AUTH_ERROR', false));
      }
      throw error;
    }
  }

  @Action
  async refreshSSOSession(): Promise<boolean> {
    const email = localStorage.getItem('sso.email');
    if (!email) {
      return true;
    }

    try {
      // Include the current Firebase ID token to prove the caller is authenticated.
      // The axios interceptor skips auth headers for /public/ URLs, so we add it explicitly.
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return false;
      }
      const idToken = await currentUser.getIdToken();
      const response = await ajax.post('/api/v1/public/auth/entra/refresh', { email }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      const { firebaseCustomToken } = response.data;

      // Sign in to Firebase with the new custom token
      const userCredential = await signInWithCustomToken(auth, firebaseCustomToken);
      this.context.commit("setCurrentUser", userCredential.user);

      // Update validation timestamp
      localStorage.setItem('sso.lastValidatedAt', new Date().toISOString());
      return true;
    } catch (error: any) {
      console.error('[SSO Refresh] Failed:', error);
      // Clear SSO data and force re-login
      localStorage.removeItem('sso.lastValidatedAt');
      localStorage.removeItem('sso.email');
      return false;
    }
  }

  @Action
  async triggerPasswordReset(request: TriggerPasswordResetRequest) {
    await ajax.post('/api/v1/sys/trigger-password-reset', request)
    .then(response => {
      this.context.commit("setPasswordReset", true);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  async loginAnon (request: AnonAuthenticateRequest) {
    await ajax.post('/api/v1/sys/anon-authenticate', request)
    .then(response => {
      this.context.commit("persistJwtToken", response.data.jwtToken);
    })
    .catch(error => {
      WorkerAnonModule.handleApiError(error);
    });
  }

  @Action
  async getUserPermissionsCompany() {
    await ajax.get('/api/v1/agency/companies')
    .then(response => {
      this.context.commit("persistUserPermissionsCompany", response.data);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  async getUserProfile() {
    await ajax.get('/api/v1/sys/user-profile')
    .then(response => {
      this.context.commit("persistUserProfile", response.data);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }
  
  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  // @Action
  // public async setCurrentAgencyContext(agencyId: string) {
  //   this.context.commit("storeCurrentAgencyContext", agencyId);
  // }

  @Mutation
  public async setErrorMessage(errorMessage: ErrorMessage) {
    this.lastError = errorMessage;
    //We need to set this to false first to ensure it's value changes (could have been canceled by dissmissable or programmatically)
    this.lastErrorVisible = false;
    if (errorMessage != null) {
      this.lastErrorVisible = true;
    } 
  }

  @Mutation
  public async setSysAdminScope() {
    this.navigationMenu = new NavigationMenu();
    this.navigationMenu.scope = NavigationScope.SysAdmin;
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/agencies", "Agencies", IconConstants.Agency));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/apps", "Apps", IconConstants.App));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/table-of-contents", "Table of Contents", "mdi-format-list-bulleted-type"));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/knowledge-base", "Articles", "mdi-book-open-variant"));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/media-gallery", "Media Library", "mdi-image-multiple"));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/worker-users", "Worker Users", IconConstants.User));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/sys-admin-users", "Sys Admin Users", IconConstants.User));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/agentic-tasks", "Agentic Tasks", IconConstants.Robot));
    this.navigationMenu.sysAdminMenuItems.push(new NavigationMenuItem("/sysadmin/migrations", "Migrations", "mdi-database-cog-outline"));

    
    localStorage.setItem('app.navigationMenu', JSON.stringify(this.navigationMenu));
  }

  @Mutation
public async setAgencyScope() {
    console.log("setAgencyScope");
    console.log(JSON.stringify(this.currentAgencyContext))
    this.navigationMenu = new NavigationMenu();
    this.navigationMenu.scope = NavigationScope.Agency;
    const items = this.navigationMenu.agencyMenuItems;
    // Dashboard and Schedule are always visible (landing pages for every user).
    items.push(new NavigationMenuItem("/agency/dashboard", "Dashboard", IconConstants.Dashboard));
    if (canView(Permissions.Activities)) items.push(new NavigationMenuItem("/agency/activities", "Activities", IconConstants.Activity));
    if (canView(Permissions.Opportunities)) items.push(new NavigationMenuItem("/agency/opportunities", "Opportunities", IconConstants.Opportunity));
    if (canView(Permissions.Companies)) items.push(new NavigationMenuItem("/agency/companies", this.currentAgencyContext.labels.companies, IconConstants.Company));
    if (canView(Permissions.Locations)) items.push(new NavigationMenuItem("/agency/locations", this.currentAgencyContext.labels.locations, IconConstants.Location));
    if (canView(Permissions.Contacts)) items.push(new NavigationMenuItem("/agency/contacts", "Contacts", IconConstants.Contact));
    if (canView(Permissions.Workers)) items.push(new NavigationMenuItem("/agency/workers", this.currentAgencyContext.labels.workers, IconConstants.Worker));
    if (canView(Permissions.Campaigns)) items.push(new NavigationMenuItem("/agency/campaigns", "Campaigns", IconConstants.Campaign));
    if (canView(Permissions.SpecOuts)) items.push(new NavigationMenuItem("/agency/spec-outs", "Spec Outs", IconConstants.SpecOut));
    if (canView(Permissions.Jobs)) items.push(new NavigationMenuItem("/agency/jobs", "Jobs", IconConstants.Job));
    if (canView(Permissions.Bookings)) items.push(new NavigationMenuItem("/agency/bookings", "Bookings", IconConstants.Booking));
    // Invoices / Payroll only appear when the matching integration is
    // configured for the agency (flags ride on the agency context).
    if (canView(Permissions.Invoices) && this.currentAgencyContext.isAccountIntegrationEnabled) items.push(new NavigationMenuItem("/agency/invoices", "Invoices", IconConstants.Invoice));
    if (canView(Permissions.Payroll) && this.currentAgencyContext.isPayrollIntegrationConfigured) items.push(new NavigationMenuItem("/agency/payroll", "Payroll", "mdi-cash-multiple"));
    if (canView(Permissions.Messaging)) items.push(new NavigationMenuItem("/agency/messaging", "Messaging", IconConstants.Conversation));
    if (canView(Permissions.Timesheets)) items.push(new NavigationMenuItem("/agency/timesheets", "Timesheets", IconConstants.Timesheet));
    if (canView(Permissions.Compliance)) items.push(new NavigationMenuItem("/agency/compliance", "Compliance", IconConstants.Compliance));
    if (canView(Permissions.Reports)) items.push(new NavigationMenuItem("/agency/reports", "Reports", IconConstants.Reports));
    // Settings is restricted to administrators only. Non-admin users still get
    // view permission on individual settings entities so dropdowns work across
    // the app, but the Settings area itself is hidden to avoid confusion.
    if (this.userProfile && (this.userProfile.userType === 'SysAdmin' || this.userProfile.isAdministrator)) {
      items.push(new NavigationMenuItem("/agency/settings", "Settings", IconConstants.Settings));
    }
    localStorage.setItem('app.navigationMenu', JSON.stringify(this.navigationMenu));
  }

  @Mutation
  public async setCompanyScope(company: CompanyDto) {
    this.navigationMenu = new NavigationMenu();
    this.navigationMenu.scope = NavigationScope.Company;
    this.navigationMenu.companyMenuItems.push(new NavigationMenuItem(`/companies/${company.id}/bookings`, "Bookings", IconConstants.Booking));
    this.navigationMenu.companyMenuItems.push(new NavigationMenuItem(`/companies/${company.id}/reports`, "Reports", IconConstants.Reports));
    this.navigationMenu.companyMenuItems.push(new NavigationMenuItem(`/companies/${company.id}/settings`, "Settings", IconConstants.Settings));
    localStorage.setItem('app.navigationMenu', JSON.stringify(this.navigationMenu));
  }

  @Mutation
  public setNavigationDrawerIsMini(navigationDrawerIsMini: boolean) {
    this.navigationDrawerIsMini = navigationDrawerIsMini;
    localStorage.setItem('app.navigationDrawerIsMini', JSON.stringify(navigationDrawerIsMini));
  }

  // @Mutation
  // public async storeCurrentAgencyContext(agencyId: string) {
  //   this.agencyIdentifiers.forEach(agency => {
  //       if (agency.id === agencyId) {
  //         this.currentAgencyContext = agency;
  //         localStorage.setItem('app.currentAgencyContext', JSON.stringify(agency));
  //       }
  //     });
  // }

  @Mutation
  public async persistJwtToken(response: JwtToken) {
    if (response) {
      const tokenExpireDate = new Date(new Date().getTime() + 1000 * 20000);//response.jwtToken.expireInSeconds);
      cookieUtils.setCookieValue('portal.giganaut.com.AuthToken', response.token, tokenExpireDate, cookieUtils.appPath(), undefined);
    } else {
      cookieUtils.deleteCookie('portal.giganaut.com.AuthToken', undefined);
    }
  }

  @Mutation
  public async persistAgencyContext(response: AgencyContextDto) {
    //HACK - this dosn't work...
    //this.setCurrentAgencyContext(response.agencyIdentifier.id);
    //so...
    this.currentAgencyContext = response;
    localStorage.setItem('app.currentAgencyContext', JSON.stringify(response));

  }

  // @Mutation
  // public async persistAuthenticationResponse(response: AuthenticateResponseModel) {
  //   if (response) {
  //     const tokenExpireDate = new Date(new Date().getTime() + 1000 * 20000);//response.jwtToken.expireInSeconds);
  //     cookieUtils.setCookieValue('portal.giganaut.com.AuthToken', response.jwtToken.token, tokenExpireDate, cookieUtils.appPath(), undefined);
  //   } else {
  //     cookieUtils.deleteCookie('portal.giganaut.com.AuthToken', undefined);
  //   }
  //   console.log(response);
  //   this.userType = response.userType;
  //   localStorage.setItem('app.userType', JSON.stringify(response.userType));
  //   this.agencyIdentifiers = response.agencyIdentifiers;
  //   localStorage.setItem('app.agencyIdentifiers', JSON.stringify(response.agencyIdentifiers));
  //   if (this.agencyIdentifiers && this.agencyIdentifiers.length === 1) {
  //     this.currentAgencyContext = this.agencyIdentifiers[0];
  //     localStorage.setItem('app.currentAgencyContext', JSON.stringify(this.agencyIdentifiers[0]));
  //   }
  // }

  @Mutation 
  persistUserProfile(response: UserProfileDto) {
    this.userProfile = response;
    localStorage.setItem('app.userProfile', JSON.stringify(response));
  }

  @Mutation 
  setPasswordReset(passwordReset: boolean) {
    this.passwordReset = passwordReset;
  }

  @Mutation
  resetState() {
    const auth = getAuth();
    signOut(auth);
    this.currentUser = null;
    localStorage.removeItem('app.userProfile');
    this.userProfile = null;
    localStorage.removeItem('app.navigationMenu');
    this.navigationMenu = null;
    localStorage.removeItem('app.navigationDrawerIsMini');
    this.navigationDrawerIsMini = null;
    localStorage.removeItem('app.currentAgencyContext');
    this.currentAgencyContext = null;
    localStorage.removeItem('sso.lastValidatedAt');
    localStorage.removeItem('sso.email');
    cookieUtils.deleteCookie('portal.giganaut.com.AuthToken', undefined);
    this.passwordReset = false;
  }

  @Mutation
  persistRoles() {
    // if (data && data.success) {
    //   state.roles = data.roles;
    //   const rolesExpireDate = new Date(new Date().getTime() + 1000 * data.expireInSeconds);
    //   abpUtils.setCookieValue('Abp.Roles', JSON.stringify(data.roles), rolesExpireDate, abpUtils.appPath());
    //   console.log('saved roles');
    // } else {
    //   console.log(data);
    // }
  }

  @Mutation
  setCurrentUser(currentUser: FirebaseUser) {
    this.currentUser = currentUser;
  }

  @Mutation
  setAuthChecked(checked: boolean) {
    this.authChecked = checked;
  }
}

export const AppModule = getModule(App)

